import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Label as LabelIcon,
  FilterAlt as FilterIcon,
  Assignment as HelperSheetIcon,
} from '@mui/icons-material';
import {
  useTable,
  SortingState,
  RowSelectionState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import { useGetDevicesQuery, useGenerateDeviceLabelsMutation } from '@store/api/deviceApi';
import { calculateMacBasedSerial } from '../utils/deviceSerial';
import { buildHelperSheetHtml } from '../utils/labelHelperSheet';
import ColumnSelector from '../components/common/ColumnSelector';
import {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
} from '../components/common/DataTable';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { LABEL_COLUMNS, type LabelDevice } from '../components/labels/labelColumns';
import {
  LABEL_COLUMN_METADATA,
  LABEL_DEFAULT_COLUMNS,
} from '../components/labels/labelColumnMetadata';

const LabelManagementPage: React.FC = () => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [generatingLabels, setGeneratingLabels] = useState<'0000006047' | '0000006049' | null>(null);
  const [showAlreadyLabeled, setShowAlreadyLabeled] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const { data: devicesResponse, isLoading: loading, isFetching: refreshing, refetch } =
    useGetDevicesQuery({ page: page + 1, pageSize }); // API uses 1-based pagination
  const [generateDeviceLabels] = useGenerateDeviceLabelsMutation();

  // Map the shared Device shape onto this view's label-focused interface.
  const devices: LabelDevice[] = useMemo(
    () =>
      (devicesResponse?.data ?? []).map((device) => ({
        id: device.id,
        deviceId: device.deviceId,
        name: device.name || device.deviceId,
        macAddress: device.macAddress || 'N/A',
        secureSerial: device.secureSerial?.toString() || 'N/A',
        secureCode: device.secureCode || 'N/A',
        registrationStatus: (device.registrationStatus as LabelDevice['registrationStatus']) || 'pending',
        createdAt: device.createdAt,
        lastLabelGeneratedAt: device.lastLabelGeneratedAt ?? null,
        status: device.status || 'unknown',
      })),
    [devicesResponse]
  );

  const totalDevices = devicesResponse?.total ?? 0;

  const isDeviceSelectable = (device: LabelDevice) => {
    const hasCompleteData = (
      device.registrationStatus === 'verified' &&
      device.macAddress !== 'N/A' &&
      device.secureSerial !== 'N/A' &&
      device.secureCode !== 'N/A'
    );

    // If showAlreadyLabeled is false, exclude devices that already have labels
    if (!showAlreadyLabeled && device.lastLabelGeneratedAt) {
      return false;
    }

    return hasCompleteData;
  };

  // Rows the filter lets through. Selection is kept outside the table (keyed by
  // deviceId), so a device stays selected even once the filter hides its row.
  const visibleDevices = useMemo(
    () => devices.filter((device) => showAlreadyLabeled || !device.lastLabelGeneratedAt),
    [devices, showAlreadyLabeled]
  );

  // TanStack removes deselected keys rather than setting them false.
  const selectedDeviceIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);
  const selectedCount = selectedDeviceIds.length;

  const {
    visibleColumns,
    toggleColumn,
    resetToDefault,
    applyPreset,
    columnVisibility,
  } = useColumnVisibility({
    columns: LABEL_COLUMN_METADATA,
    storageKey: 'labelManagement.columnVisibility.v1',
    defaultColumns: LABEL_DEFAULT_COLUMNS,
  });

  const table = useTable({
    features,
    data: visibleDevices,
    columns: LABEL_COLUMNS,
    state: { rowSelection, sorting, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    // Key selection by deviceId so it survives re-fetches and page changes.
    getRowId: (row) => row.deviceId,
    // Only verified devices with complete identity data can be labelled.
    enableRowSelection: (row) => isDeviceSelectable(row.original),
    // Sorting applies to the page that is loaded; the server owns pagination.
    manualPagination: true,
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setRowSelection({}); // Clear selection when changing pages
  };

  const handleChangeRowsPerPage = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
    setRowSelection({}); // Clear selection when changing page size
  };

  const generateLabels = async (partNo: '0000006047' | '0000006049') => {
    if (selectedCount === 0) {
      alert('Please select at least one device');
      return;
    }

    setGeneratingLabels(partNo);
    try {
      const responseBlob = await generateDeviceLabels({
        deviceIds: selectedDeviceIds,
        partNo,
      }).unwrap();

      // Create download link for PDF file
      const blob = new Blob([responseBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `device_labels_${partNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`Generated ${selectedCount} labels for ${partNo === '0000006047' ? 'EU' : 'US'} region`);

      // The mutation invalidates the device list, which refetches updated timestamps.
      // Selection is kept on purpose: Helper Sheet and the other region's labels
      // only exist while devices are selected, and both are wanted after the PDF.
    } catch (error) {
      console.error('Failed to generate labels:', error);
      alert('Failed to generate labels');
    } finally {
      setGeneratingLabels(null);
    }
  };

  const getSelectedDevicesData = () => {
    return devices
      .filter((device) => rowSelection[device.deviceId])
      .map((device) => ({
        ...device,
        // Empty/N/A MACs render as "N/A" on the printed sheet (unchanged behavior).
        macBasedSerial: calculateMacBasedSerial(device.macAddress) || 'N/A',
      }));
  };

  const printHelperSheet = () => {
    const helperWindow = window.open('', '_blank');
    if (!helperWindow) return;

    helperWindow.document.write(buildHelperSheetHtml(getSelectedDevicesData()));
    helperWindow.document.close();
  };

  const selectableDevicesCount = devices.filter(isDeviceSelectable).length;

  const emptyMessage =
    devices.length === 0
      ? 'No devices found. Register some devices to start generating labels.'
      : 'No unlabeled devices. Toggle "Show devices with labels" to view them.';

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <LabelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Device Label Management
        </Typography>
        <Typography variant="body1" sx={{
          color: 'text.secondary'
        }}>
          Generate and manage device labels with MAC addresses, serial numbers, and secure codes.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            {/* A metric value is not a heading — MUI would otherwise emit <h6>. */}
            <Typography variant="h6" component="p">
              {totalDevices}
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Total Devices
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" component="p">
              {devices.filter(d => d.registrationStatus === 'verified').length}
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Verified (This Page)
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="h6" component="p">{devices.filter(d => !d.lastLabelGeneratedAt && d.registrationStatus === 'verified').length}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Unlabeled Devices
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" component="p">
              {devices.filter(d => d.lastLabelGeneratedAt).length}
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary'
            }}>
              Labels Generated (This Page)
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            {selectedCount} device{selectedCount !== 1 ? 's' : ''} selected
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              ml: 1
            }}>
            ({showAlreadyLabeled
              ? `Showing all ${devices.length} devices`
              : `Showing ${devices.filter(d => !d.lastLabelGeneratedAt).length} unlabeled devices`})
          </Typography>
          {selectedCount > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<HelperSheetIcon />}
                onClick={printHelperSheet}
                sx={{ borderColor: 'primary.main', color: 'primary.main' }}
              >
                Helper Sheet
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PrintIcon />}
                onClick={() => generateLabels('0000006047')}
                disabled={generatingLabels !== null}
              >
                {generatingLabels === '0000006047' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Generate EU Labels'
                )}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={() => generateLabels('0000006049')}
                disabled={generatingLabels !== null}
              >
                {generatingLabels === '0000006049' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Generate US Labels'
                )}
              </Button>
              {/* Only way to clear once the rows have left the filtered view. */}
              <Button
                variant="text"
                onClick={() => setRowSelection({})}
                disabled={generatingLabels !== null}
              >
                Clear selection
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAlreadyLabeled}
                onChange={(e) => {
                  setShowAlreadyLabeled(e.target.checked);
                  // Clear selection when toggling to avoid confusion
                  setRowSelection({});
                }}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FilterIcon fontSize="small" />
                <Typography variant="body2">
                  Show devices with labels
                </Typography>
              </Box>
            }
          />
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {selectableDevicesCount === 0 && !showAlreadyLabeled && devices.some(d => d.lastLabelGeneratedAt) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          All unlabeled devices have been processed. Toggle "Show already labeled" to view or regenerate labels for devices.
        </Alert>
      )}

      {selectableDevicesCount === 0 && showAlreadyLabeled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No devices are ready for label generation. Devices must be verified and have complete identity data.
        </Alert>
      )}

      {/* Device Table */}
      <Paper sx={{ width: '100%' }}>
        <DataTableToolbar
          actions={
            <ColumnSelector
              columns={LABEL_COLUMN_METADATA}
              visibleColumns={visibleColumns}
              onColumnToggle={toggleColumn}
              onReset={resetToDefault}
              onPresetSelect={applyPreset}
            />
          }
        />

        <DataTable
          table={table}
          ariaLabel="Device labels"
          isLoading={loading}
          emptyMessage={emptyMessage}
          // Devices that cannot be labelled stay visible but read as inactive.
          getRowSx={(device) => (isDeviceSelectable(device) ? {} : { opacity: 0.6 })}
        />

        <DataTablePagination
          count={totalDevices}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default LabelManagementPage;
